import {
  Pipe,
  PipeTransform,
  OnDestroy,
  ChangeDetectorRef,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { of, Subscription } from 'rxjs';
import { switchMap, take, map } from 'rxjs/operators';
interface TranslationRef<T extends string | string[]> {
  key?: T;
  defaultKey?: string;
  value: T;
  interpolateParams?: object;
}
@Pipe({
  name: 'cignaTranslateOr',
  pure: false,
})
export class TranslateOrPipe<T extends string | string[]>
  implements PipeTransform, OnDestroy
{
  private _sub?: Subscription;
  private _current: TranslationRef<T> = { value: this.getDefaultValue() };

  constructor(
    private _translate: TranslateService,
    private _ref: ChangeDetectorRef,
  ) {}

  ngOnDestroy() {
    this.clearSub();
  }

  transform(key: T, defaultKey: string, interpolateParams?: object): T {
    if (
      this._current.key === key &&
      this._current.defaultKey === defaultKey &&
      this._current.interpolateParams === interpolateParams
    ) {
      return this._current.value;
    }

    this._current = {
      key,
      defaultKey,
      interpolateParams,
      value: this.getDefaultValue(),
    };

    this.clearSub();

    this._sub = this._translate
      .stream(key, interpolateParams)
      .pipe(
        switchMap((translation: string | Record<string, string>) =>
          hasKeysMissing(translation, key)
            ? this._translate.stream(defaultKey, interpolateParams).pipe(
                take(1),
                map((value: string) =>
                  typeof translation === 'string' ? value : [value],
                ),
              )
            : typeof translation === 'string'
            ? of(translation)
            : of(Object.values(translation)),
        ),
      )
      .subscribe((value: T) => {
        this._current.value = value;
        this._ref.markForCheck();
      });
    return this._current.value;
  }

  private clearSub() {
    if (this._sub) {
      this._sub.unsubscribe();
    }
  }
  private getDefaultValue() {
    return '' as T;
  }
}
function hasKeysMissing(
  translation: string | Record<string, string>,
  key: string | string[],
) {
  if (typeof translation === 'string' || typeof key === 'string') {
    return translation === key;
  }
  return key.some((k) => translation[k] === k);
}
